import { CircleDotIcon, PauseIcon, Trash2Icon } from 'lucide-react';
import { Badge } from '@/components/ui/badge.js';
import { cn } from '@/lib/utils.js';

export type Status = 'active' | 'paused' | 'deleted';

/*
 * Each state gets its own background, its own glyph and its own word, because
 * colour is never allowed to be the only signal.
 *
 * `deleted` is neutral rather than red on purpose: it is a resting state, not
 * an error, and spending the danger colour on it would leave nothing louder
 * for a redirect that is actually broken.
 */
const STATES = {
  active: { variant: 'ok', label: 'Active', Icon: CircleDotIcon },
  paused: { variant: 'warn', label: 'Paused', Icon: PauseIcon },
  deleted: { variant: 'secondary', label: 'Deleted', Icon: Trash2Icon },
} as const;

export type StatusBadgeProps = {
  status: Status;
  /** Overrides the default word — e.g. 'Live' where the row means traffic. */
  label?: string;
  className?: string;
};

export function StatusBadge({ status, label, className }: StatusBadgeProps) {
  const { variant, label: fallback, Icon } = STATES[status];
  return (
    <Badge variant={variant} data-status={status} className={cn(className)}>
      <Icon aria-hidden="true" />
      {label ?? fallback}
    </Badge>
  );
}
